import { ParsedCSVData, ValidationError } from '@/types/upload';
import { CornerBlockTypeSchema, CornerItemTypeSchema } from '@/types/api.zod';
import { type CsvRow, toCsvRow } from '@/utils/csvRow';

/**
 * トップページ（コーナー）CSV のクライアント側バリデーション。
 *
 * S3 へ presigned URL でアップロードする前に、Book / Product / Menu と同様に全ルールを
 * クライアントで検証してプレビュー可否を判定する。サーバー（ProcessUpload Lambda /
 * CornerUploadService）でも同一ルールを再検証する（多層防御）。
 *
 * 仕様の正本: docs/features/corner.md「CSV フォーマット」「CSV バリデーションルール」。
 * block_type / item_type の許容値は codegen の Zod enum（単一の出所）から導出する。
 *
 * 権威はサーバー（corner_service.py）側にあり、ここはアップロード前の UX 向け事前検証。
 * slug / platform / generate_type / 日時等のルールはバックエンドと手動ミラーのため、
 * 仕様変更時は docs/features/corner.md を基点に corner_service.py と本ファイルの両方を更新すること。
 */

const COLUMNS = [
  'data_type',
  'corner_name',
  'slug',
  'block_id',
  'start_at',
  'stop_at',
  'title',
  'block_type',
  'generate_type',
  'block_link_to',
  'block_link_target',
  'block_link_txt',
  'item_type',
  'item',
  'item_link_to',
  'item_link_target',
  'platform',
  'note',
] as const;

// 列名をキーに持つ 1 行分の型（値は常に string）。詳細は utils/csvRow.ts。
type CornerColumn = (typeof COLUMNS)[number];
type CornerRow = CsvRow<typeof COLUMNS>;

// data_type 別の required / not_include（docs/features/corner.md）。
// 列名を CornerColumn に固定しているため、COLUMNS に無い列名を書くとコンパイルエラーになる。
type RowRule = { required: CornerColumn[]; notInclude: CornerColumn[] };
const ROW_RULES: Record<string, RowRule> = {
  PAGE: {
    required: ['corner_name', 'slug', 'start_at'],
    notInclude: [
      'block_id',
      'block_type',
      'block_link_to',
      'block_link_target',
      'block_link_txt',
      'generate_type',
      'platform',
      'item_type',
      'item',
      'item_link_to',
      'item_link_target',
    ],
  },
  BLOCK: {
    required: ['block_id', 'block_type', 'generate_type', 'platform'],
    notInclude: ['slug', 'corner_name', 'item_type', 'item', 'item_link_to', 'item_link_target'],
  },
  ITEM: {
    required: ['item', 'item_type'],
    notInclude: [
      'slug',
      'corner_name',
      'block_id',
      'block_type',
      'block_link_to',
      'block_link_target',
      'block_link_txt',
      'generate_type',
    ],
  },
};

const SLUG_RE = /^[a-z0-9_-]+$/;
const PLATFORM_RE = /^[01]{3}$/;
const AUTO_BLOCK_IDS: readonly string[] = ['new_comic', 'new_magazine'];

const CSV_DATETIME_RE = /^(\d{4})[/-](\d{1,2})[/-](\d{1,2})(?:[T ](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?$/;
// フォールバックはタイムゾーン付き ISO8601 のみ許容（bare "2026" 等は弾く。
// バックエンド datetime.fromisoformat + astimezone 相当）。日付部の存在チェック用に年月日をキャプチャする。
const ISO8601_TZ_RE = /^(\d{4})-(\d{2})-(\d{2})[T ]\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})$/;
const DATETIME_FORMAT_HINT = '（例: 2026-06-01 / 2026/06/01 00:00 / 2026-06-01T00:00:00+09:00）';

const BLOCK_TYPES: readonly string[] = CornerBlockTypeSchema.options;
const ITEM_TYPES: readonly string[] = CornerItemTypeSchema.options;

// 存在しない日付（例: 2026-02-29 / 2026-06-31）を UTC ロールオーバーの有無で弾く
const isRealDate = (year: number, month: number, day: number): boolean => {
  const probe = new Date(Date.UTC(year, month - 1, day));
  return probe.getUTCFullYear() === year && probe.getUTCMonth() === month - 1 && probe.getUTCDate() === day;
};

const isValidDateTime = (value: string): boolean => {
  const s = value.trim();
  const m = CSV_DATETIME_RE.exec(s);
  if (m) {
    const [, y, mo, d, h, mi, sec] = m;
    const hour = Number(h ?? 0);
    const minute = Number(mi ?? 0);
    const second = Number(sec ?? 0);
    // 時刻の範囲チェック（例: 99:99 を弾く。サーバの strptime と一致）
    if (hour > 23 || minute > 59 || second > 59) return false;
    return isRealDate(Number(y), Number(mo), Number(d));
  }
  // フォールバックは TZ 付き ISO8601 のみ（bare "2026" 等は許容しない）。
  // Date.parse は存在しない日付（2026-02-29 等）を翌日へロールオーバーして受理するため、
  // 日付部は isRealDate で明示的に検証する（バックエンド fromisoformat の拒否と一致させる）。
  const iso = ISO8601_TZ_RE.exec(s);
  if (!iso) return false;
  const [, y, mo, d] = iso;
  return isRealDate(Number(y), Number(mo), Number(d)) && !Number.isNaN(Date.parse(s));
};

const err = (row: number, column: string, value: string, error: string): ValidationError => ({
  row,
  column,
  value,
  error,
});

const validateRequiredNotInclude = (
  row: number,
  dataType: string,
  rule: RowRule,
  d: CornerRow,
  errors: ValidationError[],
): void => {
  rule.required.forEach((col) => {
    if (!d[col]) errors.push(err(row, col, d[col], `${dataType} には ${col} が必須です`));
  });
  rule.notInclude.forEach((col) => {
    if (d[col]) errors.push(err(row, col, d[col], `${dataType} 行に ${col} は指定できません`));
  });
};

// 必須チェックは validateRequiredNotInclude に集約。以下は形式のみ（値がある時だけ）検証。
const validateDateTime = (row: number, value: string, field: string, errors: ValidationError[]): void => {
  if (value && !isValidDateTime(value)) {
    errors.push(err(row, field, value, `${field} の日時形式が不正です${DATETIME_FORMAT_HINT}`));
  }
};

const validatePlatform = (row: number, value: string, errors: ValidationError[]): void => {
  if (!value) return;
  if (!PLATFORM_RE.test(value)) {
    errors.push(err(row, 'platform', value, 'platform は 3 桁（web/ios/android, 0/1）で指定してください'));
  } else if (value === '000') {
    errors.push(err(row, 'platform', value, 'platform は 000 不可（最低 1 つは表示）'));
  }
};

const validatePageRow = (row: number, d: CornerRow, errors: ValidationError[]): void => {
  if (d.slug && !SLUG_RE.test(d.slug)) {
    errors.push(err(row, 'slug', d.slug, 'slug は a-z / 0-9 / _ / - のみ使用できます'));
  }
  validateDateTime(row, d.start_at, 'start_at', errors);
  validateDateTime(row, d.stop_at, 'stop_at', errors);
};

const validateBlockRow = (row: number, d: CornerRow, errors: ValidationError[]): void => {
  if (d.block_id && !SLUG_RE.test(d.block_id)) {
    errors.push(err(row, 'block_id', d.block_id, 'block_id は a-z / 0-9 / _ / - のみ使用できます'));
  }
  if (d.generate_type && d.generate_type !== 'auto' && d.generate_type !== 'manual') {
    errors.push(err(row, 'generate_type', d.generate_type, 'generate_type は auto / manual のいずれかです'));
  } else if (d.generate_type === 'auto' && d.block_id && !AUTO_BLOCK_IDS.includes(d.block_id)) {
    errors.push(
      err(row, 'block_id', d.block_id, `generate_type=auto の block_id は ${AUTO_BLOCK_IDS.join(' / ')} のみ可`),
    );
  }
  if (d.block_type && !BLOCK_TYPES.includes(d.block_type)) {
    errors.push(err(row, 'block_type', d.block_type, `block_type は ${BLOCK_TYPES.join(' / ')} のいずれかです`));
  }
  // 「もっと見る」文言（block_link_txt）があるのに遷移先（block_link_to）が空だと導線が機能しないため弾く。
  if (d.block_link_txt && !d.block_link_to) {
    errors.push(
      err(row, 'block_link_to', d.block_link_to, 'block_link_txt（もっと見る）指定時は block_link_to（遷移先）が必須です'),
    );
  }
  validatePlatform(row, d.platform, errors);
  validateDateTime(row, d.start_at, 'start_at', errors);
  validateDateTime(row, d.stop_at, 'stop_at', errors);
};

const validateItemRow = (row: number, d: CornerRow, errors: ValidationError[]): void => {
  if (d.item_type && !ITEM_TYPES.includes(d.item_type)) {
    errors.push(err(row, 'item_type', d.item_type, `item_type は ${ITEM_TYPES.join(' / ')} のいずれかです`));
  }
  // item_type=banner は遷移先（item_link_to）が無いとバナー導線が機能しないため弾く（SBI #450）。
  if (d.item_type === 'banner' && !d.item_link_to) {
    errors.push(err(row, 'item_link_to', d.item_link_to, 'item_type=banner の場合は item_link_to（遷移先）が必須です'));
  }
  validatePlatform(row, d.platform, errors);
  validateDateTime(row, d.start_at, 'start_at', errors);
  validateDateTime(row, d.stop_at, 'stop_at', errors);
};

export const validateCornerCsv = (data: ParsedCSVData): ValidationError[] => {
  const errors: ValidationError[] = [];

  // ヘッダー（順序込み完全一致）
  const headerMatches =
    data.headers.length === COLUMNS.length && COLUMNS.every((c, i) => data.headers[i] === c);
  if (!headerMatches) {
    return [err(1, 'ヘッダー', data.headers.join(','), `CSVヘッダーが不正です。期待: ${COLUMNS.join(',')}`)];
  }

  if (data.rows.length === 0) {
    return [err(1, 'データ', '', 'データ行がありません（1=定義名 / 2=表示名 / 3 行目以降=データ）')];
  }

  let pageCount = 0;
  let blockCount = 0;
  let currentBlockIsAuto = false;
  let sawBlock = false;
  const seenBlockIds = new Set<string>();

  // parseCSV が 2 行目（表示名）をスキップ済みのため data.rows[0] は実ファイル 3 行目に相当する。
  data.rows.forEach((row, idx) => {
    const rowNum = idx + 3;

    if (row.length !== COLUMNS.length) {
      errors.push(err(rowNum, '列数', row.join(','), `列数が不正です（${row.length} 列、期待 ${COLUMNS.length} 列）`));
      return;
    }

    const d = toCsvRow(COLUMNS, row);

    const rule = ROW_RULES[d.data_type];
    if (!rule) {
      errors.push(err(rowNum, 'data_type', d.data_type, 'data_type が不正です（PAGE / BLOCK / ITEM のみ）'));
      return;
    }

    validateRequiredNotInclude(rowNum, d.data_type, rule, d, errors);

    switch (d.data_type) {
      case 'PAGE':
        pageCount += 1;
        if (rowNum !== 3) errors.push(err(rowNum, 'data_type', 'PAGE', 'PAGE 行は 3 行目のみに置けます'));
        validatePageRow(rowNum, d, errors);
        break;
      case 'BLOCK':
        sawBlock = true;
        blockCount += 1;
        currentBlockIsAuto = d.generate_type === 'auto';
        if (d.block_id) {
          if (seenBlockIds.has(d.block_id)) {
            errors.push(err(rowNum, 'block_id', d.block_id, `block_id が重複しています: '${d.block_id}'`));
          }
          seenBlockIds.add(d.block_id);
        }
        validateBlockRow(rowNum, d, errors);
        break;
      case 'ITEM':
        validateItemRow(rowNum, d, errors);
        if (!sawBlock) {
          errors.push(err(rowNum, 'data_type', 'ITEM', 'ITEM 行が BLOCK より前に存在します（PAGE 直下不可）'));
        } else if (currentBlockIsAuto) {
          errors.push(
            err(rowNum, 'data_type', 'ITEM', 'generate_type=auto の BLOCK 直後に ITEM 行は置けません（auto は自動生成）'),
          );
        }
        break;
    }
  });

  // 構造（行跨ぎ）
  if (pageCount === 0) {
    errors.push(err(1, 'data_type', '', 'PAGE 行がありません（CSV につき 1 行必須・3 行目）'));
  } else if (pageCount > 1) {
    errors.push(err(1, 'data_type', 'PAGE', 'PAGE 行は 1 行のみ可'));
  }
  if (blockCount === 0) {
    errors.push(err(1, 'data_type', '', 'BLOCK 行がありません（最低 1 ブロック必須）'));
  }

  return errors;
};
